import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { InsuranceCoverageMonitor } from "../../../../../../services/api/insurance/monitor/types";
import {
  getRequirementDefinitionTableCellProps,
  getRequirementDefinitionTemplateByParams
} from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { getInsuranceCoverageProgressSteps } from "../helpers/getInsuranceCoverageProgressSteps";
import { getInsuranceCoverageRequirementName } from "../helpers/getInsuranceCoverageRequirementName";
import { getInsuranceCoveragePayerRequirementStatus } from "../helpers/getInsuranceCoveragePayerRequirementStatus";
import { ProcessingDetails } from "./ProcessingDetails";

import { InsuranceCoverageDetailsWidgetContainer } from "../../components/InsuranceCoverageDetailsWidgetContainer";
import { InsuranceCoverageDetails } from "../../components/InsuranceCoverageDetails";
import { RequirementInstructionsView } from "../../../../components/RequirementInstructionsView/RequirementInstructionsView";
import { selectorPolicyTypesFromSettings } from "../../selectors/selectorPolicyTypesFromSettings";

const DEFAULT_NAME = "Insurance Coverage";

export const usePayerInsuranceCoverageConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  InsuranceCoverageMonitor | undefined
> => {
  return {
    isActionRequired: () => {
      return false;
    },
    requirementName: (requirement, context, insuranceCoverage) =>
      getInsuranceCoverageRequirementName(
        insuranceCoverage,
        requirement.name || DEFAULT_NAME
      ),

    requirementInstructions: (requirement, context, insuranceCoverage) => {
      return instructions => {
        const templateInstructions = getRequirementDefinitionTemplateByParams({
          type: requirement.requirementType,
          policyType: selectorPolicyTypesFromSettings(
            insuranceCoverage?.settings
          )
        }).instructions;

        return (
          <RequirementInstructionsView
            instructions={templateInstructions || instructions || ""}
          />
        );
      };
    },

    requirementDetailsProgressWidget: (
      requirement,
      context,
      insuranceCoverage
    ) => {
      const steps = getInsuranceCoverageProgressSteps(insuranceCoverage);
      return steps;
    },

    requirementDetailsWidgets(_requirement, _context, insuranceCoverage) {
      return insuranceCoverage ? (
        <InsuranceCoverageDetailsWidgetContainer
          insuranceCoverage={insuranceCoverage}
          context={RequirementContextType.Payer}
          isInitialLoading={false}
        />
      ) : null;
    },
    requirementDetails: (requirement, context, insuranceCoverage) => {
      if (requirement.payerOverrideStatus === "Incomplete") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payer}
            description="The policy you submitted for client approval was rejected"
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage!}
            pill={{
              theme: "error",
              text: "Rejected",
              icon: true
            }}
          />
        );
      } else if (
        insuranceCoverage?.status === "Pending" ||
        insuranceCoverage?.status === "Monitoring"
      ) {
        return {
          header: {
            label: {
              text: "Action required"
            },
            value: {
              pill: {
                theme: "neutral",
                text: "Not started",
                icon: "block"
              }
            }
          },
          divider: false,
          data: [],
          children: (
            <ProcessingDetails
              requirement={requirement}
              insuranceCoverageMonitor={insuranceCoverage}
              callback={callback}
            />
          )
        };
      } else if (insuranceCoverage?.status === "Complete") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payer}
            description="A policy was found in your resources that matches the client requirement."
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage}
            pill={{
              theme: "success",
              text: "Passed",
              icon: true
            }}
          />
        );
      } else if (insuranceCoverage?.status === "ForceMatched") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payer}
            description="You have opted to submit a partially matched policy for client approval."
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage}
            pill={{
              theme: "warning",
              text: "Partial match",
              icon: true
            }}
          />
        );
      }

      return {
        data: [],
        children: <></>
      };
    },

    toolbarActions: (requirement, _context, insuranceCoverage) => {
      return [];
    },

    nameCell: (requirement, _context, insuranceCoverage) => ({
      text: getInsuranceCoverageRequirementName(
        insuranceCoverage,
        requirement.name || DEFAULT_NAME
      )
    }),

    typeCell: (requirement, context, insuranceCoverage) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType,
        policyType: selectorPolicyTypesFromSettings(insuranceCoverage?.settings)
      }),

    statusCell: (requirement, context, insuranceCoverage) => {
      return getInsuranceCoveragePayerRequirementStatus(
        requirement,
        insuranceCoverage
      );
    }
  };
};
